import React, {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { GridSortModel } from '@mui/x-data-grid-pro'
import { getPartnerId } from 'src/selectors/user'
import { TeamMemberInvite, TeamMemberInviteDetails } from 'src/interfaces/team'
import { handleApiError } from 'src/utils/errorHandlers'
import { TeamContext } from '../../Team.context'
import { TeamTableHeader } from '../../grid/TeamTableHeader'
import { FetchTeamMemberInvite, FetchTeamMemberInvites } from 'src/constants/actionTypes'
import { TeamMemberInviteActionMenu } from './TeamMemberInviteActionMenu'
import {
  ITeamMemberInvitesTableContext,
  TeamMemberInvitesTableContext,
} from './TeamMemberInvitesTable.context'
import {
  teamMemberInvitesActionColumn,
  teamMemberInvitesBaseColumns,
} from 'src/constants/team/team-member-invites/teamMemberInvitesTableGrid'
import { TeamMemberInviteDetailsModal } from '../../team-member-invite-details/TeamMemberInviteDetailsModal'
import { getTeamMemberInvitesCount } from 'src/selectors/team'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { useFormatMarketsData } from 'src/utils/hooks/useFormatMarketsData'

interface FetchITeamMemberInvitesParams {
  searchQuery: string
  sortModel?: GridSortModel
}

const getRowId = (row: TeamMemberInvite) => row.id

export interface TeamMemberInvitesTableRef {
  refreshTeamMemberInvites: () => Promise<void>
}

interface Props {}

const TeamMemberInvitesTableComponent = forwardRef<TeamMemberInvitesTableRef, Props>(({}, ref) => {
  const dispatch = useDispatch()

  const partnerId = useSelector(getPartnerId)
  const teamMemberInvitesCount = useSelector(getTeamMemberInvitesCount) || 0

  const { hasPermission } = useCheckPermissions()
  const formatMarkets = useFormatMarketsData()

  const { searchQuery } = useContext(TeamContext)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [teamMemberInvites, setTeamMemberInvites] = useState<TeamMemberInvite[]>([])
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)
  const [selectedTeamMemberInviteId, setSelectedTeamMemberInviteId] = useState<string | null>(null)
  const [teamMemberInviteToAction, setTeamMemberInviteToAction] = useState<TeamMemberInvite | null>(
    null,
  )
  const [teamMemberInviteDetails, setTeamMemberInviteDetails] =
    useState<TeamMemberInviteDetails | null>(null)

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const fetchTeamMemberInvites = useCallback(
    async ({ searchQuery, sortModel }: FetchITeamMemberInvitesParams) => {
      if (!partnerId) {
        return
      }

      setIsLoading(true)

      try {
        const params: any = {}

        if (searchQuery) {
          params.search = searchQuery
        }

        if (sortModel && sortModel.length > 0) {
          params.sort = sortModel
            .map((item) => `${item.field}:${item.sort?.toUpperCase()}`)
            .join(',')
        }

        const payload: { data: TeamMemberInvite[] } = await createAsyncAction(
          dispatch,
          FetchTeamMemberInvites.request(params),
        )

        setTeamMemberInvites(payload.data)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [partnerId],
  )

  const refreshTeamMemberInvites = useCallback(async () => {
    await fetchTeamMemberInvites({
      searchQuery,
      sortModel,
    })
  }, [searchQuery, sortModel])

  useEffect(() => {
    fetchTeamMemberInvites({
      searchQuery,
      sortModel,
    })
  }, [partnerId, searchQuery, sortModel])

  const openTeamMemberInviteItemAction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, teamMemberInviteToEdit: TeamMemberInvite) => {
      setActionAnchor(event.currentTarget)
      setTeamMemberInviteToAction(teamMemberInviteToEdit)
    },
    [],
  )

  const closeTeamMemberInviteItemAction = useCallback(() => {
    setActionAnchor(null)
    setTeamMemberInviteToAction(null)
  }, [])

  const teamMemberInvitesContextValue = useMemo<ITeamMemberInvitesTableContext>(() => {
    return {
      refreshTeamMemberInvites,

      actionAnchor,
      teamMemberInviteToAction,
      openTeamMemberInviteItemAction,
      closeTeamMemberInviteItemAction,
    }
  }, [
    refreshTeamMemberInvites,

    actionAnchor,
    teamMemberInviteToAction,
    openTeamMemberInviteItemAction,
    closeTeamMemberInviteItemAction,
  ])

  const handleRowClick = useCallback(({ row }: { row: TeamMemberInvite }) => {
    setSelectedTeamMemberInviteId(row.id)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedTeamMemberInviteId(null)
  }, [])

  const fetchTeamMemberInviteDetails = useCallback(async (teamMemberInviteId: string) => {
    try {
      const teamMemberInvite: TeamMemberInviteDetails = await createAsyncAction(
        dispatch,
        FetchTeamMemberInvite.request({ teamMemberInviteId }),
      )

      setTeamMemberInviteDetails(teamMemberInvite)
    } catch (err) {
      handleApiError(err)
    }
  }, [])

  const refreshTeamMemberInviteDetails = useCallback(async () => {
    if (selectedTeamMemberInviteId) {
      await fetchTeamMemberInviteDetails(selectedTeamMemberInviteId)
    }

    refreshTeamMemberInvites()
  }, [selectedTeamMemberInviteId, refreshTeamMemberInvites])

  useEffect(() => {
    if (!selectedTeamMemberInviteId) {
      setTeamMemberInviteDetails(null)
    } else {
      fetchTeamMemberInviteDetails(selectedTeamMemberInviteId)
    }
  }, [selectedTeamMemberInviteId])

  useImperativeHandle(
    ref,
    () => ({
      refreshTeamMemberInvites,
    }),
    [refreshTeamMemberInvites],
  )

  const columns = useMemo(() => {
    const cols = teamMemberInvitesBaseColumns.map((col) => {
      if (col.field === 'markets') {
        return {
          ...col,
          valueGetter: (params: any) => {
            if (params.row.allMarkets) {
              return 'All markets'
            }

            return formatMarkets(params.row.marketIds).join(', ')
          },
        }
      }

      return col
    })

    if (hasPermission(UserPermission.InvitesCreate)) {
      cols.push(teamMemberInvitesActionColumn)
    }

    return cols
  }, [hasPermission, formatMarkets])

  return (
    <TeamMemberInvitesTableContext.Provider value={teamMemberInvitesContextValue}>
      <TeamTableHeader isShowStatusSelect={false} />
      <GridTable<TeamMemberInvite>
        rows={teamMemberInvites}
        rowHeight={66}
        isLoading={isLoading}
        rowCount={teamMemberInvitesCount}
        columns={columns}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
        getRowId={getRowId}
      />
      <TeamMemberInviteActionMenu />
      {teamMemberInviteDetails && (
        <TeamMemberInviteDetailsModal
          teamMemberInviteDetails={teamMemberInviteDetails}
          onClose={handleCloseDetails}
          onRefreshTeamMemberInviteDetails={refreshTeamMemberInviteDetails}
          onRefreshTeamMemberInvites={refreshTeamMemberInvites}
        />
      )}
    </TeamMemberInvitesTableContext.Provider>
  )
})

export const TeamMemberInvitesTable = memo(TeamMemberInvitesTableComponent)
